import React, { useState, useEffect } from 'react';
import { Send, MessageCircle, CheckCircle, XCircle, RefreshCw, Phone, Clock } from 'lucide-react';

const WhatsAppDashboard = () => {
  const [config, setConfig] = useState({
    workerUrl: '',
    accessToken: '',
    phoneNumberId: '',
    recipientNumber: ''
  });
  
  const [testMessage, setTestMessage] = useState('');
  interface Message {
    id: number;
    text: string;
    from: string;
    type: string;
    timestamp: string;
  }
  
  const [messages, setMessages] = useState<Message[]>([]);
  const [status, setStatus] = useState({ connected: false, checking: false });
  const [sending, setSending] = useState(false);

  // Simulate webhook messages (in production, this would come from your worker)
  const addMessage = (text: string, from: string, type = 'received') => {
    setMessages(prev => [{
      id: Date.now(),
      text,
      from,
      type,
      timestamp: new Date().toLocaleTimeString()
    }, ...prev].slice(0, 50));
  };

  const checkConnection = async () => {
    setStatus({ connected: false, checking: true });
    
    try {
      // Use environment variable or fallback
      const apiUrl = import.meta.env.VITE_API_URL || 'https://biig-api.b-9f2.workers.dev';
      const response = await fetch(`${apiUrl}/api/whatsapp/status`);
      
      if (response.ok) {
        const data = await response.json();
        setStatus({ 
          connected: data.connected, 
          checking: false 
        });
      } else {
        setStatus({ connected: false, checking: false });
      }
    } catch (error) {
      console.error('Connection check failed:', error);
      setStatus({ connected: false, checking: false });
    }
  };

  const sendTestMessage = async () => {
    if (!testMessage || !config.recipientNumber) return;
    
    setSending(true);
    
    try {
      const apiUrl = 'https://biig-api.b-9f2.workers.dev';
      const response = await fetch(`${apiUrl}/api/whatsapp/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          to: config.recipientNumber,
          message: testMessage
        })
      });
      
      if (response.ok) {
        addMessage(testMessage, 'You', 'sent');
        setTestMessage('');
        
        // Simulate receiving AI response (in production, this would come via webhook)
        setTimeout(() => {
          addMessage('AI is processing your message...', 'System', 'received');
        }, 1000);
      } else {
        console.error('Failed to send message');
      }
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    checkConnection();
  }, [config.accessToken, config.phoneNumberId]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-green-500 rounded-xl">
                <MessageCircle className="text-white" size={28} />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">WhatsApp Bot Dashboard</h1>
                <p className="text-gray-500">Monitor and test your WhatsApp integration</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {status.checking ? (
                <RefreshCw className="animate-spin text-gray-400" size={20} />
              ) : status.connected ? (
                <CheckCircle className="text-green-500" size={20} />
              ) : (
                <XCircle className="text-red-500" size={20} />
              )}
              <span className="text-sm font-medium">
                {status.checking ? 'Checking...' : status.connected ? 'Connected' : 'Disconnected'}
              </span>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Configuration Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <Phone size={20} />
              Configuration
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Worker URL
                </label>
                <input
                  type="text"
                  placeholder="https://your-bot.workers.dev"
                  value={config.workerUrl}
                  onChange={(e) => setConfig({...config, workerUrl: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token
                </label>
                <input
                  type="password"
                  placeholder="Your WhatsApp API token"
                  value={config.accessToken}
                  onChange={(e) => setConfig({...config, accessToken: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone Number ID
                </label>
                <input
                  type="text"
                  placeholder="1234567890123456"
                  value={config.phoneNumberId}
                  onChange={(e) => setConfig({...config, phoneNumberId: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Test Recipient Number
                </label>
                <input
                  type="text"
                  placeholder="+1234567890"
                  value={config.recipientNumber}
                  onChange={(e) => setConfig({...config, recipientNumber: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                />
              </div>
              
              <button
                onClick={checkConnection}
                className="w-full py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors font-medium"
              >
                Test Connection
              </button>
            </div>
          </div>

          {/* Message Testing Panel */}
          <div className="bg-white rounded-2xl shadow-lg p-6 flex flex-col">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <MessageCircle size={20} />
              Test Messages
            </h2>
            
            {/* Message History */}
            <div className="flex-1 bg-gray-50 rounded-lg p-4 mb-4 max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <div className="text-center text-gray-400 py-8">
                  <MessageCircle size={48} className="mx-auto mb-2 opacity-30" />
                  <p>No messages yet</p>
                  <p className="text-sm mt-1">Send a test message to get started</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map(msg => (
                    <div
                      key={msg.id}
                      className={`flex ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-xs px-4 py-2 rounded-2xl ${
                          msg.type === 'sent' 
                            ? 'bg-green-500 text-white' 
                            : 'bg-white border border-gray-200'
                        }`}
                      >
                        <p className="text-sm">{msg.text}</p>
                        <div className={`text-xs mt-1 flex items-center gap-1 ${
                          msg.type === 'sent' ? 'text-green-100' : 'text-gray-400'
                        }`}>
                          <Clock size={10} />
                          {msg.timestamp}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Message Input */}
            <div className="flex gap-2">
              <input
                type="text"
                placeholder="Type a test message..."
                value={testMessage}
                onChange={(e) => setTestMessage(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && sendTestMessage()}
                disabled={!status.connected}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent disabled:bg-gray-100"
              />
              <button
                onClick={sendTestMessage}
                disabled={!status.connected || sending || !testMessage}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed"
              >
                {sending ? (
                  <RefreshCw className="animate-spin" size={20} />
                ) : (
                  <Send size={20} />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Info Cards */}
        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Quick Start</h3>
            <p className="text-sm text-gray-600">
              1. Deploy your Worker<br/>
              2. Add credentials<br/>
              3. Test connection<br/>
              4. Send test messages
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Message Types</h3>
            <p className="text-sm text-gray-600">
              • Text messages<br/>
              • Images & documents<br/>
              • Interactive buttons<br/>
              • Template messages
            </p>
          </div>
          
          <div className="bg-white rounded-xl shadow-md p-4">
            <h3 className="font-semibold text-gray-700 mb-2">Next Steps</h3>
            <p className="text-sm text-gray-600">
              • Add message templates<br/>
              • Implement media handling<br/>
              • Create interactive menus<br/>
              • Add AI responses
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WhatsAppDashboard;